import React, { useState, useEffect, useRef } from 'react';
import { BookOpen } from 'lucide-react';
import { toast } from 'sonner';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Card, CardHeader, CardContent } from '@/components/ui/card';
import { Combobox } from '@/components/ui/combobox';
import { getDictTableMeta, type DictTableMetaColumn } from '../../services/data-integration-api';

interface DictFieldConfigPanelProps {
  /** 当前字段的目标列名，用于自动生成伴生列名 */
  targetColumnName: string;
  /** 源库连接 ID */
  sourceConnectionId: string;
  /** 型录表列表，用于选择字典表 */
  catalogTables: Array<{ table_name: string; table_comment: string }>;
  /** 当前字典配置 */
  config: {
    dict_table_name: string;
    dict_type_column: string;
    dict_type_value: string;
    dict_code_column: string;
    dict_value_column: string;
    dict_companion_column_name: string;
  };
  /** 配置变更回调 */
  onChange: (field: string, value: string) => void;
}

/** 检查字典配置是否完整（所有必填项已填写） */
export function isDictConfigComplete(config: {
  dict_table_name: string;
  dict_code_column: string;
  dict_value_column: string;
  dict_companion_column_name: string;
}): boolean {
  return !!(
    config.dict_table_name &&
    config.dict_code_column &&
    config.dict_value_column &&
    config.dict_companion_column_name
  );
}

export default function DictFieldConfigPanel({
  targetColumnName,
  sourceConnectionId,
  catalogTables,
  config,
  onChange,
}: DictFieldConfigPanelProps) {
  const [columns, setColumns] = useState<DictTableMetaColumn[]>([]);
  const [distinctTypes, setDistinctTypes] = useState<string[]>([]);
  const [loadingCols, setLoadingCols] = useState(false);
  const [loadingTypes, setLoadingTypes] = useState(false);
  const prevCompleteRef = useRef(false);

  const isComplete = isDictConfigComplete(config);

  // 当字典表变更时，加载列信息
  useEffect(() => {
    if (!config.dict_table_name || !sourceConnectionId) {
      setColumns([]);
      setDistinctTypes([]);
      return;
    }
    (async () => {
      setLoadingCols(true);
      try {
        const result = await getDictTableMeta({
          connectionId: sourceConnectionId,
          tableName: config.dict_table_name,
        });
        setColumns(result.columns);
      } catch {
        setColumns([]);
      } finally {
        setLoadingCols(false);
      }
    })();
  }, [config.dict_table_name, sourceConnectionId]);

  // 当类型列变更时，加载 distinct 类型值
  useEffect(() => {
    if (!config.dict_table_name || !config.dict_type_column || !sourceConnectionId) {
      setDistinctTypes([]);
      return;
    }
    (async () => {
      setLoadingTypes(true);
      try {
        const result = await getDictTableMeta({
          connectionId: sourceConnectionId,
          tableName: config.dict_table_name,
          typeColumn: config.dict_type_column,
        });
        setDistinctTypes(result.distinct_types);
      } catch {
        setDistinctTypes([]);
      } finally {
        setLoadingTypes(false);
      }
    })();
  }, [config.dict_table_name, config.dict_type_column, sourceConnectionId]);

  // 自动生成伴生列名
  useEffect(() => {
    if (!config.dict_companion_column_name && targetColumnName) {
      onChange('dict_companion_column_name', `${targetColumnName}_text`);
    }
  }, [targetColumnName]);

  // 配置从不完整变完整时 toast 提示
  useEffect(() => {
    if (isComplete && !prevCompleteRef.current) {
      toast.success('字典配置已完成');
    }
    prevCompleteRef.current = isComplete;
  }, [isComplete]);

  const columnOptions = columns.map(c => ({
    value: c.column_name,
    label: c.column_name,
  }));

  const tableOptions = catalogTables.map(t => ({
    value: t.table_name,
    label: t.table_name + (t.table_comment ? ` (${t.table_comment})` : ''),
  }));

  const typeValueOptions = distinctTypes.map(t => ({
    value: t,
    label: t,
  }));

  return (
    <Card className="border-l-4 border-l-[var(--color-accent-blue)]">
      <CardHeader className="flex flex-row items-center justify-between py-2 px-4 space-y-0">
        <div className="flex items-center gap-2 text-sm font-medium">
          <BookOpen className="h-4 w-4 text-[var(--color-accent-blue)]" />
          字典翻译配置 — {targetColumnName}
        </div>
        <Badge className={
          isComplete
            ? 'bg-[var(--color-success-light)] text-[var(--color-success)]'
            : 'bg-[var(--color-warning-light)] text-[var(--color-warning)]'
        }>
          {isComplete ? '已配置' : '待完善'}
        </Badge>
      </CardHeader>
      <CardContent className="px-4 pb-3 pt-0">
        <div className="grid grid-cols-3 gap-x-4 gap-y-3">
          {/* 字典表（占两列，表名最长） */}
          <div className="col-span-2 space-y-1">
            <Label className="text-xs">字典表 <span className="text-red-500">*</span></Label>
            <Combobox
              value={config.dict_table_name}
              onChange={v => onChange('dict_table_name', v)}
              options={tableOptions}
              placeholder="选择字典表..."
              searchPlaceholder="搜索表名..."
            />
          </div>

          {/* 类型列 */}
          <div className="space-y-1">
            <Label className="text-xs">类型列</Label>
            <Combobox
              value={config.dict_type_column}
              onChange={v => onChange('dict_type_column', v)}
              options={columnOptions}
              placeholder="选择..."
              searchPlaceholder="搜索列名..."
              disabled={loadingCols || columns.length === 0}
            />
          </div>

          {/* 类型值 */}
          <div className="space-y-1">
            <Label className="text-xs">类型值</Label>
            {distinctTypes.length > 0 ? (
              <Combobox
                value={config.dict_type_value}
                onChange={v => onChange('dict_type_value', v)}
                options={typeValueOptions}
                placeholder="选择..."
                searchPlaceholder="搜索类型值..."
                disabled={loadingTypes}
              />
            ) : (
              <Input
                type="text"
                value={config.dict_type_value}
                onChange={e => onChange('dict_type_value', e.target.value)}
                placeholder="输入类型值"
                className="h-8"
              />
            )}
          </div>

          {/* 编码列 */}
          <div className="space-y-1">
            <Label className="text-xs">编码列 <span className="text-red-500">*</span></Label>
            <Combobox
              value={config.dict_code_column}
              onChange={v => onChange('dict_code_column', v)}
              options={columnOptions}
              placeholder="选择..."
              searchPlaceholder="搜索列名..."
              disabled={loadingCols || columns.length === 0}
            />
          </div>

          {/* 值列 */}
          <div className="space-y-1">
            <Label className="text-xs">值列 <span className="text-red-500">*</span></Label>
            <Combobox
              value={config.dict_value_column}
              onChange={v => onChange('dict_value_column', v)}
              options={columnOptions}
              placeholder="选择..."
              searchPlaceholder="搜索列名..."
              disabled={loadingCols || columns.length === 0}
            />
          </div>

          {/* 伴生列名（占两列） */}
          <div className="col-span-2 space-y-1">
            <Label className="text-xs">伴生列名 <span className="text-red-500">*</span></Label>
            <Input
              type="text"
              value={config.dict_companion_column_name}
              onChange={e => onChange('dict_companion_column_name', e.target.value)}
              placeholder={`${targetColumnName}_text`}
              className="h-8 font-mono"
            />
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
